import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// useInfiniteFeed — cursor state machine behind every infinite-scroll catalog.
//
// Responsibilities:
//   * load page 1 and reset whenever the filter signature (kind/sort/genre/rating)
//     changes, so a new filter can never append onto the previous result set
//   * append page N+1 on demand, de-duplicating by TMDB id across page boundaries
//   * drop responses belonging to a stale request, so a slow page-1 response
//     cannot land after the user has already switched filters
// ---------------------------------------------------------------------------

const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOADING_MORE: 'loading-more',
  READY: 'ready',
  ERROR: 'error',
};

// TMDB ids are numeric, so a plain map is a safe O(1) seen-set without needing Set.
const appendUnique = (incoming, seen) => {
  const fresh = [];
  for (let i = 0; i < incoming.length; i += 1) {
    const item = incoming[i];
    if (!item || seen[item.id]) continue;
    seen[item.id] = true;
    fresh.push(item);
  }
  return fresh;
};

// TMDB can keep reporting "more pages" while every result is filtered out by the
// hi/gu + released-only rules. Without a stop condition the observer would hammer
// the API all the way to the page-500 ceiling, so give up after a few barren pages.
const BARREN_PAGE_LIMIT = 3;

/**
 * @param {object} options
 * @param {'movie'|'tv'} options.kind
 * @param {Function} options.fetcher  Stable fetcher: ({page, sort, genreId, minRating}) => Promise<{items, hasMore, totalResults}>
 * @param {string} [options.sort]     UI sort id; 'newest' keeps newest content on top
 * @param {string|number} [options.genreId]
 * @param {number} [options.minRating]
 * @param {boolean} [options.enabled]
 */
const useInfiniteFeed = ({
  kind,
  fetcher,
  sort = 'newest',
  genreId = 'all',
  minRating = 0,
  enabled = true,
}) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalResults, setTotalResults] = useState(0);

  const itemsRef = useRef([]);
  const seenRef = useRef({});
  const cursorRef = useRef(0);
  const barrenRunRef = useRef(0);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const loadPage = useCallback(
    async ({ replace }) => {
      const nextPage = cursorRef.current + 1;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setStatus(nextPage === 1 ? STATUS.LOADING : STATUS.LOADING_MORE);
      setError(null);

      try {
        const result = await fetcherRef.current({ page: nextPage, sort, genreId, minRating });

        // A newer request (or an unmount, or a filter change) already owns the state.
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;

        const fresh = appendUnique(result.items || [], seenRef.current);
        itemsRef.current = replace ? fresh : itemsRef.current.concat(fresh);
        cursorRef.current = result.page || nextPage;
        barrenRunRef.current = fresh.length === 0 ? barrenRunRef.current + 1 : 0;

        setItems(itemsRef.current);
        setHasMore(Boolean(result.hasMore) && barrenRunRef.current < BARREN_PAGE_LIMIT);
        setTotalResults(
          typeof result.totalResults === 'number' ? result.totalResults : itemsRef.current.length
        );
        setStatus(STATUS.READY);
      } catch (err) {
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;
        setError(err instanceof Error ? err : new Error('Unable to load content from TMDB'));
        setStatus(STATUS.ERROR);
      }
    },
    [sort, genreId, minRating]
  );

  const resetAndLoad = useCallback(() => {
    itemsRef.current = [];
    seenRef.current = {};
    cursorRef.current = 0;
    barrenRunRef.current = 0;
    requestIdRef.current += 1; // orphan whatever was in flight before the reset
    setItems([]);
    setHasMore(true);
    setTotalResults(0);
    return loadPage({ replace: true });
  }, [loadPage]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // `loadPage` identity changes with sort/genreId/minRating, so this doubles as
  // "reset and refetch whenever the filters change".
  useEffect(() => {
    if (!enabled || !fetcher) return undefined;
    resetAndLoad();
    return undefined;
  }, [enabled, fetcher, resetAndLoad]);

  const isBusy = status === STATUS.LOADING || status === STATUS.LOADING_MORE;

  const loadMore = useCallback(() => {
    if (!enabled || !hasMore || isBusy) return;
    loadPage({ replace: false });
  }, [enabled, hasMore, isBusy, loadPage]);

  const retry = useCallback(() => {
    if (!enabled) return;
    resetAndLoad();
  }, [enabled, resetAndLoad]);

  return {
    items,
    status,
    error,
    hasMore,
    totalResults,
    isInitialLoading: status === STATUS.LOADING,
    isLoadingMore: status === STATUS.LOADING_MORE,
    isEmpty: status === STATUS.READY && items.length === 0,
    loadMore,
    retry,
  };
};

export { STATUS as FEED_STATUS };
export default useInfiniteFeed;
